import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useOnboardingStore } from "../stores/onboarding-store";
import { CHECKLIST_ITEMS } from "../types";

function ProgressRing({ completed, total, size = 28 }: { completed: number; total: number; size?: number }) {
	const strokeWidth = 3;
	const radius = (size - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;
	const progress = total > 0 ? completed / total : 0;
	const offset = circumference * (1 - progress);

	return (
		<svg width={size} height={size} className="shrink-0 -rotate-90">
			<circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth={strokeWidth} />
			<circle
				cx={size / 2}
				cy={size / 2}
				r={radius}
				fill="none"
				stroke="white"
				strokeWidth={strokeWidth}
				strokeDasharray={circumference}
				strokeDashoffset={offset}
				strokeLinecap="round"
				className="transition-all duration-700 ease-out"
			/>
		</svg>
	);
}

function CheckmarkIcon({ animate }: { animate: boolean }) {
	return (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" className={`shrink-0 ${animate ? "animate-check-pop" : ""}`}>
			<circle cx="10" cy="10" r="10" fill="#111827" />
			<path
				d="M6 10l3 3 5-6"
				stroke="white"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
				className={animate ? "animate-check-draw" : ""}
				strokeDasharray={animate ? "20" : undefined}
				strokeDashoffset={animate ? "0" : undefined}
			/>
		</svg>
	);
}

export default function OnboardingChecklist() {
	const { brandUrl } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const { completedSteps, lastCompletedStep, loading, popoverOpen, fetch, setPopoverOpen } = useOnboardingStore();
	const popoverRef = useRef<HTMLDivElement>(null);
	const [buttonBounce, setButtonBounce] = useState(false);
	const prevCompletedRef = useRef<number>(0);

	const workspaceId = brandUrl;

	useEffect(() => {
		if (workspaceId) {
			fetch(workspaceId);
		}
	}, [workspaceId, fetch]);

	const completedCount = CHECKLIST_ITEMS.filter((item) => completedSteps.includes(item.key)).length;
	const totalCount = CHECKLIST_ITEMS.length;
	const remaining = totalCount - completedCount;

	// Trigger bounce animation on the button when a step completes
	useEffect(() => {
		if (completedCount > prevCompletedRef.current && prevCompletedRef.current > 0) {
			setButtonBounce(true);
			const timer = setTimeout(() => setButtonBounce(false), 1000);
			return () => clearTimeout(timer);
		}
		prevCompletedRef.current = completedCount;
	}, [completedCount]);

	useEffect(() => {
		if (!popoverOpen) return;
		function handleClickOutside(e: MouseEvent) {
			if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
				setPopoverOpen(false);
			}
		}
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [popoverOpen, setPopoverOpen]);

	// Hide pill on dashboard — tiles are shown there instead
	const isDashboard = location.pathname === `/${brandUrl}` || location.pathname === `/${brandUrl}/dashboard`;

	if (loading || !workspaceId || isDashboard) return null;

	const allComplete = completedCount === totalCount;

	if (allComplete) return null;

	return (
		<>
			<style>{`
				@keyframes check-pop {
					0% { transform: scale(0); opacity: 0; }
					50% { transform: scale(1.3); }
					70% { transform: scale(0.9); }
					100% { transform: scale(1); opacity: 1; }
				}
				@keyframes check-draw {
					0% { stroke-dashoffset: 20; }
					100% { stroke-dashoffset: 0; }
				}
				@keyframes button-bounce {
					0%, 100% { transform: scale(1); }
					20% { transform: scale(1.15); }
					40% { transform: scale(0.95); }
					60% { transform: scale(1.05); }
					80% { transform: scale(0.98); }
				}
				.animate-check-pop {
					animation: check-pop 0.5s ease-out forwards;
				}
				.animate-check-draw {
					animation: check-draw 0.4s ease-out 0.2s forwards;
					stroke-dashoffset: 20;
				}
				.animate-button-bounce {
					animation: button-bounce 0.6s ease-out;
				}
			`}</style>
			<div ref={popoverRef} className="fixed bottom-20 right-[80px] z-50">
				<button
					onClick={() => setPopoverOpen(!popoverOpen)}
					className={`relative flex items-center justify-center gap-2 rounded-full bg-gray-900 h-[50px] pl-3 pr-5 shadow-md hover:bg-gray-800 transition-colors ${buttonBounce ? "animate-button-bounce" : ""}`}
				>
					{remaining > 0 && (
						<span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-red-500 text-white text-xs font-bold shadow-sm">
							{remaining}
						</span>
					)}
					<ProgressRing completed={completedCount} total={totalCount} />
					<span className="text-base font-semibold text-white">Get started</span>
				</button>

				{popoverOpen && (
					<div className="absolute right-0 bottom-full mb-2 w-96 rounded-xl bg-white shadow-xl border border-gray-200 overflow-hidden">
						<div className="px-5 pt-5 pb-3">
							<h3 className="text-sm font-semibold text-gray-900">Getting Started</h3>
						</div>

						<div className="mx-5 pb-4">
							<div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
								<div
									className="h-full rounded-full bg-gray-900 transition-all duration-700 ease-out"
									style={{ width: `${(completedCount / totalCount) * 100}%` }}
								/>
							</div>
							<p className="text-xs text-gray-500 mt-1">
								{completedCount} of {totalCount} completed
							</p>
						</div>

						<div className="h-px bg-gray-200" />

						<div className="px-3 py-3">
							{CHECKLIST_ITEMS.map((item) => {
								const done = completedSteps.includes(item.key);
								const justCompleted = lastCompletedStep === item.key;
								return (
									<button
										key={item.key}
										onClick={() => {
											navigate(`/${brandUrl}/${item.route}`);
											setPopoverOpen(false);
										}}
										className="flex items-start gap-3 w-full px-2 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
									>
										<div className="mt-0.5">
											{done ? (
												<CheckmarkIcon animate={justCompleted} />
											) : (
												<div className="w-5 h-5 rounded-full border-2 border-gray-300 shrink-0" />
											)}
										</div>
										<div>
											<p className={`text-sm font-medium transition-all duration-300 ${done ? "text-gray-400 line-through" : "text-gray-700"}`}>{item.label}</p>
											<p className="text-xs text-gray-400 mt-0.5">{item.description}</p>
										</div>
									</button>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</>
	);
}
